import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMemberAuth, MEMBER_TOKEN_KEY } from "../context/MemberAuthContext";
import { api } from "../lib/api";
import { CREST_URL } from "../components/SiteLayout";
import { toast } from "sonner";
import { LogOut, CheckCircle2, AlertCircle, Clock, ExternalLink } from "lucide-react";

const MemberPortalPage = () => {
  const { member, loading, logout } = useMemberAuth();
  const [status, setStatus] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const nav = useNavigate();

  useEffect(() => {
    if (loading) return;
    if (!member) {
      nav("/member-login");
      return;
    }
    const token = localStorage.getItem(MEMBER_TOKEN_KEY);
    api
      .get("/members/me", { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setStatus(res.data))
      .catch(() => toast.error("Could not load your dues status"))
      .finally(() => setLoadingStatus(false));
  }, [member, loading, nav]);

  const handleLogout = () => {
    logout();
    nav("/");
  };

  const handlePayClick = async () => {
    if (!status) return;
    const token = localStorage.getItem(MEMBER_TOKEN_KEY);
    api
      .post("/members/dues/payment-clicked", null, { headers: { Authorization: `Bearer ${token}` } })
      .catch(() => {});
    const note = encodeURIComponent(
      `GODSC ${status.settings.period_label} Dues - ${member.name}`
    );
    const url = `https://cash.app/$${status.settings.cashapp_handle}/${status.settings.amount}?note=${note}`;
    window.open(url, "_blank", "noopener,noreferrer");
    // Optimistic update
    if (!status.payment_clicked_at) {
      setStatus({ ...status, payment_clicked_at: new Date().toISOString() });
    }
  };

  if (loading || !member) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7]">
        <div className="overline text-[#5C5C5C]">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F1E6]" data-testid="member-portal-page">
      {/* Top bar */}
      <div className="bg-[#0A1F44] text-white">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="portal-logo">
            <img src={CREST_URL} alt="GODSC" className="h-10 w-10 object-contain" />
            <div>
              <div className="font-serif-display text-xl leading-none">GODSC</div>
              <div className="text-[10px] tracking-[0.28em] uppercase text-[#D4AF37] mt-1">
                Members Portal
              </div>
            </div>
          </Link>
          <button
            onClick={handleLogout}
            className="inline-flex items-center gap-2 px-3 py-2 border border-[#D4AF37] text-[#D4AF37] text-xs tracking-[0.22em] uppercase hover:bg-[#D4AF37] hover:text-black transition-colors"
            data-testid="member-logout"
          >
            <LogOut size={14} /> Sign Out
          </button>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-12">
        <div className="overline text-[#C9A227]">Welcome Back</div>
        <h1 className="font-serif-display text-4xl md:text-5xl text-[#0A1F44] mt-2">
          {member.name}
        </h1>
        <div className="text-sm tracking-[0.22em] uppercase text-[#5C5C5C] mt-2">
          {member.edition} Edition
        </div>
        <div className="h-[2px] w-16 bg-[#D4AF37] mt-4" />

        {loadingStatus ? (
          <div className="mt-10 text-[#5C5C5C]">Loading your dues status...</div>
        ) : status ? (
          <DuesCard status={status} onPay={handlePayClick} />
        ) : (
          <div className="mt-10 text-red-700">Could not load dues status.</div>
        )}
      </div>
    </div>
  );
};

const DuesCard = ({ status, onPay }) => {
  const { settings, paid, payment_clicked_at, verified_at } = status;
  const amount = settings.amount;
  const periodLabel = settings.period_label;
  const duesUnset = !amount || amount <= 0;

  return (
    <div className="mt-10 bg-white border border-[#D8D3C4]" data-testid="dues-card">
      <div className="p-8 md:p-10">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <div className="overline text-[#C9A227]">Annual Dues</div>
            <div className="font-serif-display text-3xl text-[#0A1F44] mt-1">
              {periodLabel}
            </div>
          </div>
          <StatusBadge paid={paid} clicked={payment_clicked_at} />
        </div>

        <div className="mt-8 grid sm:grid-cols-3 gap-6 pt-8 border-t border-[#D8D3C4]">
          <Stat label="Amount" value={duesUnset ? "TBD" : `$${amount}`} />
          <Stat
            label="Status"
            value={paid ? "Paid" : payment_clicked_at ? "Awaiting Verification" : "Not Paid"}
            valueColor={paid ? "text-green-700" : payment_clicked_at ? "text-[#8A6E14]" : "text-red-700"}
          />
          <Stat
            label="Verified On"
            value={verified_at ? new Date(verified_at).toLocaleDateString() : "\u2014"}
          />
        </div>

        {paid ? (
          <div className="mt-8 bg-green-50 border border-green-300 px-5 py-4 flex items-center gap-3 text-green-800" data-testid="dues-paid-banner">
            <CheckCircle2 size={20} />
            <div className="text-sm">
              Your <strong>{periodLabel}</strong> dues have been verified as paid.
              Thank you for keeping the Club strong.
            </div>
          </div>
        ) : duesUnset ? (
          <div className="mt-8 bg-[#FBF6E4] border border-[#D4AF37] px-5 py-4 text-sm text-[#0A1F44]" data-testid="dues-tbd-banner">
            The annual dues amount has not yet been set for this period. Please check back later.
          </div>
        ) : (
          <div className="mt-8 bg-[#0A1F44] text-white p-6 md:p-8 border border-[#D4AF37]/40">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-5">
              <div>
                <div className="overline text-[#D4AF37]">Pay Your Dues</div>
                <div className="font-serif-display text-3xl mt-1">
                  ${amount} via Cash App
                </div>
                <div className="text-sm text-[#D8D3C4] mt-2">
                  Cash App will open in a new tab. Sent to{" "}
                  <span className="text-[#D4AF37] font-medium">${settings.cashapp_handle}</span>{" "}
                  with your name pre-filled in the note.
                </div>
              </div>
              <button
                type="button"
                onClick={onPay}
                className="btn-gold inline-flex items-center gap-2 whitespace-nowrap"
                data-testid="dues-pay-btn"
              >
                Pay Dues <ExternalLink size={14} />
              </button>
            </div>
            {payment_clicked_at && (
              <div className="mt-5 text-xs text-[#D8D3C4]">
                You opened Cash App on{" "}
                {new Date(payment_clicked_at).toLocaleString()}. Once the Treasurer
                verifies the payment, your status will update to <strong className="text-[#D4AF37]">Paid</strong>.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const Stat = ({ label, value, valueColor = "text-[#0A1F44]" }) => (
  <div>
    <div className="overline text-[#5C5C5C]">{label}</div>
    <div className={`font-serif-display text-3xl mt-1 ${valueColor}`}>{value}</div>
  </div>
);

const StatusBadge = ({ paid, clicked }) => {
  if (paid) {
    return (
      <span className="inline-flex items-center gap-2 px-3 py-1.5 border border-green-700 text-green-700 bg-green-50 text-xs tracking-[0.18em] uppercase" data-testid="status-paid">
        <CheckCircle2 size={14} /> Paid
      </span>
    );
  }
  if (clicked) {
    return (
      <span className="inline-flex items-center gap-2 px-3 py-1.5 border border-[#D4AF37] text-[#8A6E14] bg-[#FBF6E4] text-xs tracking-[0.18em] uppercase" data-testid="status-clicked">
        <Clock size={14} /> Awaiting
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-2 px-3 py-1.5 border border-red-400 text-red-700 bg-red-50 text-xs tracking-[0.18em] uppercase" data-testid="status-unpaid">
      <AlertCircle size={14} /> Unpaid
    </span>
  );
};

export default MemberPortalPage;
